// App.js — portfolio page structure and homepage content
import React, { useCallback, useState } from "react";
import "devicon/devicon.min.css";
import ParticleExplosion from "./components/effects/ParticleExplosion";
import InteractiveDemo from "./sections/interactive/InteractiveDemo";
import ContactForm from "./sections/contact/ContactForm";
import NavBar from "./components/navigation/NavBar";
import AnimatedBackground from "./components/effects/AnimatedBackground";
import FloatingShapes from "./components/effects/FloatingShapes";
import EffectsToggle from "./components/navigation/EffectsToggle";
import VideoCarousel from "./sections/projects/VideoCarousel";
import GameplayArchitecture from "./sections/architecture/GameplayArchitecture";
import { Helmet } from "react-helmet";
import AboutMe from "./sections/about/AboutMe";
import SkillsEducation from "./sections/skills/SkillsEducation";
import TopButton from "./components/navigation/TopButton";
import SlideReveal from "./components/common/SlideReveal";
import ResumeModal from "./components/common/ResumeModal";
import { skills, featuredTechnologies } from "./data/portfolio-data";
import "./styles/portfolio-sections.css";
import "./styles/page-layout-and-controls.css";
import "./styles/navigation-skills-and-contact.css";
import "./styles/responsive-section-layout.css";

function App() {
  const [showAllTech, setShowAllTech] = useState(true);
  const [effectsPaused, setEffectsPaused] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
  // Static public asset: available locally and from GitHub Pages without a sign-in.
  const resumeUrl = `${process.env.PUBLIC_URL}/resume/Mingwei_Zhang_Resume.pdf`;
  const [resumeOpen, setResumeOpen] = useState(false);
  const openResume = useCallback(() => setResumeOpen(true), []);
  const closeResume = useCallback(() => setResumeOpen(false), []);

  return (
    <div className={`portfolio-v2${effectsPaused ? " effects-paused" : ""}`}>
      <Helmet>
        <meta name="viewport" content="width=device-width, initial-scale=1, minimum-scale=1, user-scalable=yes" />
      </Helmet>
      <TopButton />
      <AnimatedBackground paused={effectsPaused} />
      <FloatingShapes />
      <EffectsToggle paused={effectsPaused} onToggle={() => setEffectsPaused((previous) => !previous)} />
      <NavBar onResumeClick={openResume} />

      <main id="home" className="portfolio-main">
        <header className="portfolio-hero portfolio-hero-v8">
          <div className="portfolio-hero-intro-v8">
            <div className="portfolio-hero-portrait-v8">
              <img
                src={`${process.env.PUBLIC_URL}/img/profile-picture.png`}
                alt="Pixel-art portrait of Mingwei Zhang"
                className="rounded-circle profile-pic portfolio-avatar"
                width="260"
                height="260"
              />
            </div>
            <div className="portfolio-hero-copy-v8">
              <p className="portfolio-eyebrow pixel-text">WELCOME TO MY PORTFOLIO</p>
              <h1 className="pixel-text pixel-heading">Mingwei Zhang</h1>
              <p className="pixel-text pixel-subheading software-title">Software &amp; Game Developer</p>
              <p className="portfolio-hero-subtitle">
                Gameplay programming, interactive systems, and software development.
              </p>
              <div className="portfolio-hero-actions">
                <a
                  className="portfolio-action portfolio-action-primary"
                  href="#projects"
                  onClick={(event) => {
                    event.preventDefault();
                    document.getElementById("projects")?.scrollIntoView({
                      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
                      block: "start",
                    });
                    window.history.replaceState(null, "", "#projects");
                  }}
                >Explore projects</a>
                <button type="button" className="portfolio-hero-secondary-link-v8 portfolio-hero-resume-button" onClick={openResume} aria-haspopup="dialog">View Resume</button>
                <a className="portfolio-hero-secondary-link-v8" href="https://github.com/mingwzhang" target="_blank" rel="noopener noreferrer">GitHub</a>
              </div>
            </div>
          </div>

          <div className="portfolio-tech-summary portfolio-tech-summary-v8">
            {/* Keep the full technology list and its skills link together inside the slide. */}
            <SlideReveal id="portfolio-tech-details-v8" open={showAllTech} className="portfolio-tech-slide">
              <div className="portfolio-tech-details-v8 portfolio-tech-list">
                <div className="portfolio-tech-strip" aria-label="Highlighted technologies">
                  {featuredTechnologies.map((name) => <span key={name}>{name}</span>)}
                  <a
                    className="portfolio-all-skills-link"
                    href="#skills-section"
                    onClick={(event) => {
                      event.preventDefault();
                      document.getElementById("skills-section")?.scrollIntoView({
                        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
                        block: "start",
                      });
                      window.history.replaceState(null, "", "#skills-section");
                    }}
                  >See all skills &amp; tools</a>
                </div>
              </div>
            </SlideReveal>
            {/* The toggle remains below the animated list in both states. */}
            <button
              type="button"
              className="portfolio-tech-toggle-v8"
              onClick={() => setShowAllTech((previous) => !previous)}
              aria-expanded={showAllTech}
              aria-controls="portfolio-tech-details-v8"
            >
              <span>TECHNOLOGIES I WORK WITH</span>
              <span className="portfolio-tech-toggle-action-v8">{showAllTech ? "Hide tools −" : "Explore tools +"}</span>
            </button>
          </div>
        </header>

        <AboutMe />

        <section id="projects" className="portfolio-section portfolio-featured-section" aria-labelledby="portfolio-projects-heading">
          <div className="portfolio-section-heading">
            <span className="portfolio-kicker">01 / SELECTED WORK</span>
            <h2 id="portfolio-projects-heading" className="pixel-text pixel-bold-title">Featured Projects</h2>
            <p>Explore game footage, my role and contributions, and links to each release.</p>
          </div>
          <VideoCarousel />
        </section>

        <section id="architecture" className="portfolio-section portfolio-architecture-section" aria-labelledby="portfolio-architecture-heading">
          <div className="portfolio-section-heading">
            <span className="portfolio-kicker">02 / BEHIND THE CODE</span>
            <h2 id="portfolio-architecture-heading" className="pixel-text pixel-bold-title">Gameplay Architecture &amp; Design Patterns</h2>
            <p>Engine-independent principles, practical decisions, and reusable approaches to gameplay development.</p>
          </div>
          <GameplayArchitecture />
        </section>

        <section id="skills-section" className="portfolio-section portfolio-skills-section" aria-labelledby="portfolio-skills-heading">
          <div className="portfolio-section-heading">
            <span className="portfolio-kicker">03 / TECHNICAL BACKGROUND</span>
            <h2 id="portfolio-skills-heading" className="pixel-text pixel-bold-title">Skills &amp; Education</h2>
            <p>Programming languages, game engines, development tools, and education.</p>
          </div>
          <SkillsEducation skills={skills} />
        </section>

        <InteractiveDemo />

        <section id="contact" className="portfolio-section portfolio-contact-section" aria-labelledby="portfolio-contact-heading">
          <div className="portfolio-section-heading">
            <span className="portfolio-kicker">05 / GET IN TOUCH</span>
            <h2 id="portfolio-contact-heading" className="pixel-text pixel-bold-title">Contact</h2>
            <p>Feel free to reach out about software engineering, gameplay development, or collaboration.</p>
          </div>
          <div className="portfolio-contact-actions">
            <a href="mailto:mingw.zhang123@gmail.com" className="portfolio-action">Email</a>
            <a href="https://www.linkedin.com/in/mingwei-zhang1/" className="portfolio-action" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          </div>
          <ContactForm />
        </section>
      </main>

      <ResumeModal open={resumeOpen} onClose={closeResume} resumeUrl={resumeUrl} />
      <ParticleExplosion />
      <footer className="footer-note portfolio-footer">
        © {new Date().getFullYear()} Mingwei Zhang — Built with React.
      </footer>
    </div>
  );
}

export default App;
